import { useMemo } from "react";
import { Link } from "react-router-dom";
import { Column } from "primereact/column";
import { Dropdown } from "primereact/dropdown";
import localeText from "../../../../locale";
import CategoryService from "../../../../service/CategoryService";
import SubcategoriesService from "../../../../service/SubcategoriesService";
import useAdminList, { expectCode } from "../../hooks/useAdminList";
import useAdminAction from "../../hooks/useAdminAction";
import useCatalogForm from "../../hooks/useCatalogForm";
import { getToken, normalizeText } from "../../utils/adminUtils";
import {
  clearFilled,
  emptyTranslations,
  localizedDescription,
  missingTranslations,
  otherTranslations,
  pickTranslations,
  translationsSearchText,
  uploadCatalogImage,
} from "../../utils/catalogUtils";
import CatalogTable from "../../components/list/CatalogTable";
import CatalogThumb from "../../components/list/CatalogThumb";
import StatusBadge from "../../components/list/StatusBadge";
import FormDialog from "../../components/form/FormDialog";
import FormField from "../../components/form/FormField";
import TranslationFields from "../../components/form/TranslationFields";
import ImagePicker from "../../components/form/ImagePicker";

const LIST_PATH = "/dashboard-listsubcategories";

// Subcategorias + categorias (para saber a categoria de cada uma e montar o seletor)
const loadSubcategoriesWithCategories = async () => {
  const [subRes, catRes] = await Promise.all([
    new SubcategoriesService().getSubcategories(getToken()),
    new CategoryService().getCategories(),
  ]);
  const subcategories = expectCode(subRes, 200001).result || [];
  const categories = expectCode(catRes, 200001).result || [];

  const categoryBySubId = new Map();
  categories.forEach((category) =>
    (category.subCategories || []).forEach((sub) => categoryBySubId.set(sub.id, category)),
  );

  return [{ subcategories, categories, categoryBySubId }];
};

const AdminSubcategories = ({ startCreating = false }) => {
  const { items, loading, loadError, reload } = useAdminList(loadSubcategoriesWithCategories);
  const { run, runningId } = useAdminAction();

  const data = items[0];
  const categoryOptions = useMemo(
    () =>
      (data?.categories || [])
        .map((category) => ({ label: localizedDescription(category), value: category.id }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [data],
  );

  const buildInitial = (item) => ({
    values: item ? pickTranslations(item) : emptyTranslations(),
    categoryId: item ? data?.categoryBySubId.get(item.id)?.id ?? null : null,
    isService: !!item?.service,
    file: null,
    errors: {},
  });

  const { form, openCreate, openEdit, close, update } = useCatalogForm({
    startCreating,
    listPath: LIST_PATH,
    buildInitial,
  });

  const rows = useMemo(
    () =>
      (data?.subcategories || []).map((sub) => {
        const category = data.categoryBySubId.get(sub.id);
        const categoryName = category ? localizedDescription(category) : "";
        return {
          ...sub,
          name: localizedDescription(sub),
          categoryName,
          searchText: `${translationsSearchText(sub)} ${normalizeText(categoryName)}`,
        };
      }),
    [data],
  );

  const save = async () => {
    const errors = {};
    missingTranslations(form.values).forEach(({ field }) => {
      errors[field] = localeText("adminFormRequired");
    });
    if (!form.categoryId) errors.category = localeText("adminFormRequired");
    if (form.mode === "create" && !form.file) errors.image = localeText("pleaseUploadAtLeastOne");
    if (Object.keys(errors).length) {
      update({ errors });
      return;
    }

    const { values, file, item, categoryId, isService } = form;
    const ok = await run(
      "form",
      async () => {
        const imageid = file ? await uploadCatalogImage(file) : item.imageid;
        const service = new SubcategoriesService();
        const descriptions = [
          values.descriptionEnglish.trim(),
          values.descriptionItalian.trim(),
          values.descriptionPortuguese.trim(),
          values.descriptionSpanish.trim(),
        ];
        const res =
          form.mode === "create"
            ? await service.postRegisterSubcategory(
                getToken(),
                ...descriptions,
                categoryId,
                imageid,
                isService,
              )
            : await service.postEditSubcategory(
                getToken(),
                ...descriptions,
                categoryId,
                item.id,
                imageid,
                isService,
              );
        expectCode(res, 200000);
      },
      "adminCatalogSaved",
    );
    if (ok) {
      close();
      reload();
    }
  };

  return (
    <div className="admin-list">
      <FormDialog
        visible={!!form}
        title={localeText(form?.mode === "edit" ? "adminCatalogEditSubcategory" : "addNewSubcategory")}
        subtitle={
          form?.mode === "edit" ? localizedDescription(form.item) : localeText("subcategoryFields")
        }
        saving={runningId === "form"}
        onSave={save}
        onCancel={close}
      >
        {form && (
          <>
            <TranslationFields
              idPrefix="subcategory"
              values={form.values}
              errors={form.errors}
              onChange={(values) =>
                update({ values, errors: { ...form.errors, ...clearFilled(values) } })
              }
              autoFocus
            />
            <FormField
              label={localeText("category")}
              htmlFor="subcategory-category"
              error={form.errors.category}
              required
            >
              <Dropdown
                inputId="subcategory-category"
                value={form.categoryId}
                options={categoryOptions}
                onChange={(e) =>
                  update({ categoryId: e.value, errors: { ...form.errors, category: null } })
                }
                filter
                placeholder={localeText("adminCatalogChooseCategory")}
                panelClassName="admin-multiselect-panel"
              />
            </FormField>
            <label className="admin-checkbox">
              <input
                type="checkbox"
                checked={form.isService}
                onChange={(e) => update({ isService: e.target.checked })}
              />
              {localeText("isService")}
            </label>
            <ImagePicker
              label={localeText("subcategoryImage")}
              currentImageId={form.item?.imageid}
              file={form.file}
              required={form.mode === "create"}
              error={form.errors.image}
              onChange={(file) => update({ file, errors: { ...form.errors, image: null } })}
              onError={(message) => update({ errors: { ...form.errors, image: message } })}
            />
          </>
        )}
      </FormDialog>

      <CatalogTable
        rows={rows}
        loading={loading}
        loadError={loadError}
        onReload={reload}
        searchPlaceholder={localeText("adminCatalogSearchSubcategory")}
        createLabel={localeText("newsubcategory")}
        onCreate={openCreate}
        sortField="name"
        minWidth="52rem"
      >
        <Column
          header={localeText("subcategory")}
          field="name"
          body={(sub) => (
            <div className="admin-catalog__item">
              <CatalogThumb imageId={sub.imageid} />
              <div className="admin-stack">
                <span className="admin-strong">{sub.name || "—"}</span>
                <span className="admin-catalog__translations">{otherTranslations(sub)}</span>
              </div>
            </div>
          )}
          sortable
        />
        <Column
          header={localeText("category")}
          field="categoryName"
          body={(sub) => sub.categoryName || <span className="admin-muted">—</span>}
          sortable
        />
        <Column
          header={localeText("adminCatalogKind")}
          field="service"
          body={(sub) =>
            sub.service ? (
              <StatusBadge tone="neutral" icon="pi pi-briefcase">
                {localeText("adminCatalogService")}
              </StatusBadge>
            ) : (
              <StatusBadge tone="neutral" icon="pi pi-box">
                {localeText("adminCatalogProduct")}
              </StatusBadge>
            )
          }
          sortable
        />
        <Column
          header={localeText("actions")}
          frozen
          alignFrozen="right"
          headerClassName="admin-list__actions-cell"
          bodyClassName="admin-list__actions-cell"
          body={(sub) => (
            <div className="admin-list__header-actions">
              <Link
                className="admin-icon-btn"
                to={`/subcategory?id=${sub.id}`}
                target="_blank"
                rel="noopener noreferrer"
                title={localeText("adminCatalogViewInStore")}
                aria-label={localeText("adminCatalogViewInStore")}
              >
                <i className="pi pi-external-link" aria-hidden="true" />
              </Link>
              <button type="button" className="admin-btn" onClick={() => openEdit(sub)}>
                <i className="pi pi-pencil" aria-hidden="true" />
                {localeText("edit")}
              </button>
            </div>
          )}
        />
      </CatalogTable>
    </div>
  );
};

export default AdminSubcategories;
